import React from 'react';
import ReactDOM from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n';
import App from './App';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import './styles/design-tokens.css';
import './styles/index.css';
import { registerLicense, setCulture, setCurrencyCode } from '@syncfusion/ej2-base';

// Syncfusion Material theme CSS
import '@syncfusion/ej2-base/styles/material.css';
import '@syncfusion/ej2-buttons/styles/material.css';
import '@syncfusion/ej2-calendars/styles/material.css';
import '@syncfusion/ej2-dropdowns/styles/material.css';
import '@syncfusion/ej2-inputs/styles/material.css';
import '@syncfusion/ej2-navigations/styles/material.css';
import '@syncfusion/ej2-popups/styles/material.css';
import '@syncfusion/ej2-splitbuttons/styles/material.css';
import '@syncfusion/ej2-react-grids/styles/material.css';
// The TreeGrid sheet is NOT optional: ej2-base maps `.e-icons.e-none` to a real
// glyph (\e256), while the TreeGrid uses that same class for the invisible 10px
// indent spacers it puts in front of every tree cell. Without this sheet's
// `.e-treegrid .e-icons.e-none::before { content: "" }` those spacers render
// their glyph — which on a machine missing the icon font came out as the literal
// text "NoneNoneNone" ahead of every WBS code.
import '@syncfusion/ej2-react-treegrid/styles/material.css';
import '@syncfusion/ej2-react-pivotview/styles/material.css';
import '@syncfusion/ej2-react-gantt/styles/material.css';
// The Splitter sheet is NOT optional either, and it does NOT come with the
// Gantt: a Gantt IS a splitter — a tree grid in one pane and the timeline chart
// in the other — but the splitter lives in `ej2-layouts`, which `ej2-gantt`'s
// stylesheet does not pull in. Without it the two panes have no layout rules at
// all, so they stack vertically at full width and the chart lands on top of
// whatever follows the Gantt on the page. Only the splitter subset is imported;
// the rest of `ej2-layouts` (dashboard, timeline, card, avatar) is unused here.
import '@syncfusion/ej2-layouts/styles/splitter/material.css';
import '@syncfusion/ej2-react-schedule/styles/material.css';

// Custom Syncfusion overrides
import './styles/syncfusion-overrides.css';

// Register Syncfusion license
registerLicense(process.env.REACT_APP_SYNCFUSION_LICENSE_KEY || '');

// Set Indian locale globally for all Syncfusion components
// This ensures currency format (C0) uses ₹ symbol and Indian grouping (lakhs/crores)
setCulture('en-IN');
setCurrencyCode('INR');

// Swallow the benign "ResizeObserver loop ..." notifications. These are emitted
// by resize-aware libraries (React Flow, Syncfusion, MUI) when a layout settles
// over multiple frames; they are harmless but CRA's dev overlay surfaces them as
// runtime errors. Suppress only this specific message — nothing else.
const RESIZE_OBSERVER_MSG = /^ResizeObserver loop (limit exceeded|completed with undelivered notifications)/;
window.addEventListener('error', (e) => {
  if (e.message && RESIZE_OBSERVER_MSG.test(e.message)) {
    e.stopImmediatePropagation();
    const overlay = document.getElementById('webpack-dev-server-client-overlay');
    if (overlay) overlay.style.display = 'none';
  }
});


const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <I18nextProvider i18n={i18n}>
    <App />
  </I18nextProvider>
);

// Register PWA service worker for offline support and caching
serviceWorkerRegistration.register({
  onUpdate: (registration) => {
    // Dispatch custom event so the app can show an update notification
    const event = new CustomEvent('swUpdate', { detail: registration });
    window.dispatchEvent(event);
  },
});
